import { SparklesIcon } from "@heroicons/react/24/outline";
import { DateTime } from "luxon";

import Modal from "../../layouts/Modal";
import { IIntelligenceReviewListing, IPartIntelligenceReview } from "../../models/listingOptimisation/IListingReview";
import classNames from "../../utils/classNames";
import formatDateTime from "../../utils/formatDateTime";
import { lookupTypeLabel, normaliseLookupType } from "../../utils/intelligencePricing";
import PriceFormat from "../../utils/priceFormat";
import LoadingWheel from "../shared/LoadingWheel";

interface IIntelligenceReviewModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  review: IPartIntelligenceReview | undefined;
  isLoading: boolean;
  isError: boolean;
}

function ListingList({ listings, heading, muted = false }: { listings: IIntelligenceReviewListing[]; heading: string; muted?: boolean }) {
  return (
    <div className={classNames("flex flex-col rounded-lg border border-gray-200", muted && "opacity-70")}>
      <div className="flex items-center justify-between border-b bg-gray-50 px-3 py-2">
        <h4 className="text-sm font-semibold text-gray-800">{heading}</h4>
        <span className="text-xs text-gray-500">
          {listings.length} listing{listings.length === 1 ? "" : "s"}
        </span>
      </div>
      {listings.length === 0 ? (
        <p className="p-6 text-center text-sm text-gray-500">None</p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {listings.map((listing, index) => (
            <li key={`${listing.link ?? listing.title}-${index}`} className="p-2">
              <a
                href={listing.link ?? undefined}
                target="_blank"
                rel="noreferrer"
                className="flex items-start gap-3 rounded-md p-1 hover:bg-gray-50"
              >
                {listing.thumbnail ? (
                  <img src={listing.thumbnail} alt="" className="h-14 w-14 flex-shrink-0 rounded object-cover ring-1 ring-gray-200" loading="lazy" />
                ) : (
                  <div className="h-14 w-14 flex-shrink-0 rounded bg-gray-100 ring-1 ring-gray-200" />
                )}
                <div className="min-w-0 flex-1">
                  <p className={classNames("line-clamp-2 text-xs", muted ? "text-gray-600 line-through" : "text-gray-800")}>{listing.title}</p>
                  <p className="mt-0.5 text-[11px] text-gray-500">
                    {listing.condition}
                    {listing.isSold ? " · sold" : ""}
                  </p>
                  {listing.excludedReason && <p className="mt-0.5 text-[11px] text-amber-700">{listing.excludedReason}</p>}
                  <p className="mt-1 text-sm font-semibold tabular-nums text-gray-900">{listing.price != null ? PriceFormat(listing.price) : "—"}</p>
                </div>
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function groupBySearch(listings: IIntelligenceReviewListing[]): Array<{ heading: string; listings: IIntelligenceReviewListing[] }> {
  const groups = new Map<string, IIntelligenceReviewListing[]>();

  listings.forEach((listing) => {
    const label = lookupTypeLabel(listing.matchedOn) ?? "Other";
    const term = listing.searchTerm?.trim();
    const heading = term ? `${label} — “${term}”` : label;
    const current = groups.get(heading) ?? [];
    current.push(listing);
    groups.set(heading, current);
  });

  return [...groups.entries()].map(([heading, groupListings]) => ({ heading, listings: groupListings }));
}

/**
 * "See the N": every listing the last intelligent pricing lookup found for a part, split by the
 * eBay search that found it (MPN, make/model, OE) and then into kept vs dropped by the AI.
 */
export default function IntelligenceReviewModal({ open, setOpen, review, isLoading, isError }: IIntelligenceReviewModalProps) {
  const kept = review?.listings.filter((listing) => !listing.excludedReason) ?? [];
  const dropped = review?.listings.filter((listing) => !!listing.excludedReason) ?? [];
  const keptGroups = groupBySearch(kept);
  const droppedGroups = groupBySearch(dropped);
  const splitBySearch = keptGroups.length > 1 || kept.some((listing) => normaliseLookupType(listing.matchedOn) != null);

  return (
    <Modal open={open} setOpen={setOpen} width="max-w-2xl">
      <div className="flex items-center gap-2 border-b pb-3">
        <SparklesIcon className="h-5 w-5 text-amber-500" aria-hidden="true" />
        <h3 className="text-lg font-semibold text-gray-900">Intelligent pricing — the listings behind the figure</h3>
      </div>

      <div className="mt-4 space-y-3">
        {isLoading ? (
          <div className="flex justify-center py-10">
            <LoadingWheel />
          </div>
        ) : isError || !review ? (
          <p className="py-6 text-center text-sm text-gray-500">No intelligent pricing lookup has been run for this part yet.</p>
        ) : (
          <>
            <p className="text-sm text-gray-600">
              Looked up {formatDateTime(review.lookedUpOn, DateTime.DATETIME_MED)}
              {review.searchTerm ? (
                <>
                  {" "}
                  for <span className="font-medium text-gray-800">“{review.searchTerm}”</span>
                </>
              ) : null}
              . {review.keptCount} kept of {review.foundCount} found
              {review.aiFilterApplied ? " after the AI review" : " (no AI review)"}
              {review.average != null ? `, average ${PriceFormat(review.average)}` : ""}.
            </p>
            <div className="max-h-[60vh] space-y-3 overflow-y-auto">
              {splitBySearch
                ? keptGroups.map((group) => (
                    <ListingList key={group.heading} listings={group.listings} heading={`${group.heading} — set the price`} />
                  ))
                : <ListingList listings={kept} heading="Kept — these set the price" />}
              {dropped.length > 0 &&
                (splitBySearch
                  ? droppedGroups.map((group) => (
                      <ListingList key={`dropped-${group.heading}`} listings={group.listings} heading={`${group.heading} — dropped by the AI review`} muted />
                    ))
                  : <ListingList listings={dropped} heading="Dropped by the AI review" muted />)}
            </div>
          </>
        )}
      </div>

      <div className="mt-4 flex justify-end">
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
        >
          Close
        </button>
      </div>
    </Modal>
  );
}
